import { gql, useMutation } from '@apollo/client';

import { EPrepVersionFilesSource } from '@/graphql-types/globalTypes';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useFileUpload_UploadVersionFileV2,
  useFileUpload_UploadVersionFileV2Variables,
} from './graphql-types/useFileUpload_UploadVersionFileV2';

const FILE_UPLOAD_MUTATION = gql`
  mutation useFileUpload_UploadVersionFileV2(
    $input: PrepVersionFilesAtSourceInputV2!
  ) {
    prepVersionFilesAtSourceV2(input: $input) {
      results {
        presignedS3Url
        versionFileId
        metadataHeaders {
          name
          value
        }
      }
    }
  }
`;

export const useFileUpload = (
  source: EPrepVersionFilesSource
): {
  uploadFiles: (files: File[]) => Promise<string[]>;
  isUploading: boolean;
  numUploading: number;
  uploadProgress?: number;
} => {
  const [prepVersionFileUpload] = useMutation<
    useFileUpload_UploadVersionFileV2,
    useFileUpload_UploadVersionFileV2Variables
  >(FILE_UPLOAD_MUTATION);

  const [uploadFileToS3, { isUploading, uploadProgress, numUploading }] =
    useUploadFileToS3();

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    try {
      const { data } = await prepVersionFileUpload({
        variables: {
          input: {
            source,
            fileInfo: files.map((file) => {
              return { name: file.name, type: file.type, size: file.size };
            }),
          },
        },
      });

      if (!data?.prepVersionFilesAtSourceV2) {
        throw Error('Version file upload prep failed');
      }

      if (data?.prepVersionFilesAtSourceV2.results.length !== files.length) {
        throw new Error(`failed to prep for version file upload`);
      }

      const promisedVersionFileIds =
        data?.prepVersionFilesAtSourceV2.results.map(async (result, index) => {
          const { metadataHeaders, presignedS3Url, versionFileId } = result;

          const newMetadataHeaders: Record<string, string> = {};

          metadataHeaders?.forEach(({ name, value }) => {
            if (name && value) {
              newMetadataHeaders[name] = value;
            }
          });

          await uploadFileToS3({
            file: files[index],
            presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });

          return versionFileId;
        });

      const versionFileIds = await Promise.all(promisedVersionFileIds);
      return versionFileIds;
    } catch (err) {
      logError(err);
      return [];
    }
  };

  return { uploadFiles, isUploading, uploadProgress, numUploading };
};
